import React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_GREY_300,
  COLOR_BLUE,
  COLOR_BLUE_HOVER,
} from "../../styles/colors";
import SaveDesignModal from "./SaveDesignModal";
import MyDesignsModal from "./MyDesignsModal";
import ProblemLibraryModal from "../problems/ProblemLibraryModal";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { isAuthEnabled } from "../../config/environment";
import { useAppSelector } from "../../hooks";
import { useAuth } from "../auth/AuthProvider";

interface ScoreBoardActionsProps {
  isMobile: boolean;
  isRunning: boolean;
  isStarted: boolean;
  designName: string | null;
  /** Title of the active interview problem, whose saved attempt Clear Canvas deletes. */
  activeProblemTitle?: string | null;
  onPlayPauseClick: () => void;
  onResetConfirm: () => void;
  onNewDesignConfirm: () => void;
  onDesignSaved: (name: string) => void;
  onDesignLoaded: (name: string) => void;
  onProblemStarted: () => void;
}

const ScoreBoardActions: React.FC<ScoreBoardActionsProps> = ({
  isMobile,
  isRunning,
  isStarted,
  designName,
  activeProblemTitle,
  onPlayPauseClick,
  onResetConfirm,
  onNewDesignConfirm,
  onDesignSaved,
  onDesignLoaded,
  onProblemStarted,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);
  const [isNewDesignConfirmOpen, setIsNewDesignConfirmOpen] = React.useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
  const [isMyDesignsModalOpen, setIsMyDesignsModalOpen] = React.useState(false);
  const [isProblemLibraryOpen, setIsProblemLibraryOpen] = React.useState(false);
  const authStatus = useAppSelector((state) => state.auth.status);
  const authUser = useAppSelector((state) => state.auth.user);
  const { openAuthDialog, signOut } = useAuth();
  const isSignedIn = authStatus === "signedIn";

  const handleNewDesignClick = () => {
    if (designName !== null) {
      onNewDesignConfirm();
    } else {
      setIsNewDesignConfirmOpen(true);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const btnSize = isMobile ? "28px" : "35px";
  const iconSize = isMobile ? "16px" : "24px";

  return (
    <>
      <Box
        id="scoreboard-actions-container"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: isMobile ? "4px" : "8px",
          justifyContent: "space-evenly",
          flexGrow: 1,
        }}
      >
        <Tooltip title={!isStarted ? "Start Simulation" : (isRunning ? "Pause Simulation" : "Resume Simulation")} arrow>
          <IconButton
            aria-label="play pause"
            data-testid="play-pause-btn"
            onClick={onPlayPauseClick}
            sx={{
              backgroundColor: COLOR_PRIMARY,
              color: COLOR_PRIMARY,
              width: btnSize,
              height: btnSize,
              "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
            }}
          >
            {isRunning ? (
              <PauseIcon sx={{ color: "white", fontSize: iconSize }} />
            ) : (
              <PlayArrowIcon sx={{ color: "white", fontSize: iconSize }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="New Design" arrow>
          <IconButton
            aria-label="new design"
            data-testid="new-design-btn"
            onClick={handleNewDesignClick}
            sx={{
              backgroundColor: COLOR_PRIMARY,
              color: "white",
              width: btnSize,
              height: btnSize,
              "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
            }}
          >
            <NoteAddIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Save Design" arrow>
          <IconButton
            aria-label="save design"
            data-testid="save-design-btn"
            onClick={() => setIsSaveModalOpen(true)}
            sx={{
              backgroundColor: COLOR_PRIMARY,
              color: "white",
              width: btnSize,
              height: btnSize,
              "&:hover": { backgroundColor: COLOR_PRIMARY_DARK },
            }}
          >
            <SaveIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset Simulation" arrow>
          <IconButton
            aria-label="reset"
            data-testid="reset-btn"
            onClick={() => setIsResetConfirmOpen(true)}
            sx={{
              backgroundColor: COLOR_BLUE,
              color: "white",
              width: btnSize,
              height: btnSize,
              "&:hover": { backgroundColor: COLOR_BLUE_HOVER },
            }}
          >
            <ReplayIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Account" arrow>
          <IconButton
            aria-label="account"
            data-testid="more-options-btn"
            onClick={handleMenuOpen}
            size="medium"
            sx={{
              color: COLOR_GREY_300,
              p: "4px",
              ml: "-4px",
              "&:hover": {
                color: COLOR_PRIMARY_DARK,
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            {isSignedIn ? (
              <PersonIcon fontSize="medium" data-testid="account-icon-signed-in" />
            ) : (
              <PersonOutlineIcon fontSize="medium" data-testid="account-icon-signed-out" />
            )}
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {isSignedIn && (
            <Box sx={{ px: 2, pt: 0.5, pb: 1, maxWidth: 260 }} data-testid="account-menu-header">
              <Typography variant="caption" sx={{ color: COLOR_GREY_300, display: "block" }}>
                {authUser?.email ? "Signed in as" : "Signed in"}
              </Typography>
              {authUser?.email && (
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }} data-testid="account-email">
                  {authUser.email}
                </Typography>
              )}
            </Box>
          )}
          {!isSignedIn && isAuthEnabled() && (
            <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
              <Button
                fullWidth
                variant="contained"
                data-testid="menu-sign-in-btn"
                disabled={authStatus === "checking"}
                onClick={() => {
                  handleMenuClose();
                  openAuthDialog({ reason: "menu" });
                }}
                sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Sign in or create a free account
              </Button>
            </Box>
          )}
          {(isSignedIn || isAuthEnabled()) && <Divider />}
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setIsMyDesignsModalOpen(true);
            }}
          >
            <ListItemIcon>
              <FolderOpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Designs</ListItemText>
          </MenuItem>
          <MenuItem
            data-testid="problem-library-menu-item"
            onClick={() => {
              handleMenuClose();
              trackEvent(GA_EVENTS.PROBLEM_LIBRARY_OPENED, { source: "menu" });
              setIsProblemLibraryOpen(true);
            }}
          >
            <ListItemIcon>
              <MenuBookIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Problem Library</ListItemText>
          </MenuItem>
          {isSignedIn && <Divider />}
          {isSignedIn && (
            <MenuItem
              data-testid="sign-out-menu-item"
              onClick={() => {
                handleMenuClose();
                signOut();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sign out</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={isResetConfirmOpen}
        onClose={() => setIsResetConfirmOpen(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">{"Reset Simulation?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset the simulation? This will only reset the time and TPS stats. Your blocks, connections, settings, and traffic configuration will remain the same.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsResetConfirmOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsResetConfirmOpen(false);
              onResetConfirm();
            }}
            color="primary"
            data-testid="reset-confirm-btn"
            autoFocus
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Design Confirmation Dialog */}
      <Dialog
        open={isNewDesignConfirmOpen}
        onClose={() => setIsNewDesignConfirmOpen(false)}
        aria-labelledby="new-design-dialog-title"
        aria-describedby="new-design-dialog-description"
      >
        <DialogTitle id="new-design-dialog-title">{"Start New Design?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="new-design-dialog-description">
            Are you sure you want to start a new design? All your current blocks and
            connections in the scratchpad will be permanently removed.
            {activeProblemTitle && (
              <>
                {" "}This also deletes your saved attempt at <strong>{activeProblemTitle}</strong>.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsNewDesignConfirmOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsNewDesignConfirmOpen(false);
              onNewDesignConfirm();
            }}
            color="error"
            data-testid="new-design-confirm-btn"
            autoFocus
          >
            Clear Canvas
          </Button>
        </DialogActions>
      </Dialog>


      {/* Save Design Modal */}
      <SaveDesignModal
        open={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSaved={(name) => {
          onDesignSaved(name);
        }}
      />

      {/* My Designs Modal */}
      <MyDesignsModal
        open={isMyDesignsModalOpen}
        onClose={() => setIsMyDesignsModalOpen(false)}
        onDesignLoaded={(name) => {
          onDesignLoaded(name);
        }}
      />

      {/* Problem Library Modal */}
      <ProblemLibraryModal
        open={isProblemLibraryOpen}
        onClose={() => setIsProblemLibraryOpen(false)}
        onStarted={onProblemStarted}
      />
    </>
  );
};

export default ScoreBoardActions;
